"use client";
import React, { useState, useEffect } from "react";
import DefaultImage from "@/mocks/assets/defaultImage.png";
import Image from "next/image";
import { addToCart } from "@/utils/cartUtils";
import { v4 as uuidv4 } from "uuid";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { DropdownArrowIcon } from "@/components/sortDropdown/dropdownArrowIcon";
import { PrimaryButton } from "@/components/button/primaryButton";
import { AgeRestricted } from "@/components/ageRestricted/ageRestricted";
import { usePathname } from "next/navigation";

export const ItemPage: React.FC<any> = ({
  itemData,
  checkoutEnabled,
  isOpen,
  merchantColor,
  merchant,
}: any) => {
  const [selectedOptions, setSelectedOptions] = useState<any>({});
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<any>({});
  const [itemVariantUnavailable, setItemVariantUnavailable] =
    useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(1);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const pathName = usePathname();
  const isShopPage: boolean = pathName.includes("shop");

  useEffect(() => {
    setIsPreview(window.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    if (itemData?.items && itemData.items.length > 0) {
      const defaultItem = itemData.items[0];
      setSelectedItem(defaultItem);

      if (defaultItem.options) {
        const defaultOptions = defaultItem.options.reduce(
          (acc: any, option: any) => {
            const variant = itemData.variants.find((v: any) =>
              v.options.some((o: any) => o.id === option.id)
            );
            if (variant) {
              acc[variant.id] = option.id;
            }
            return acc;
          },
          {}
        );
        setSelectedOptions(defaultOptions);
      }
    }
  }, [itemData?.items, itemData?.variants]);

  useEffect(() => {
    if (itemData?.items) {
      const matchedItem = itemData.items.find((item: any) => {
        return item.options?.every((option: any) => {
          return Object.values(selectedOptions).includes(option.id);
        });
      });

      if (matchedItem) {
        setSelectedItem(matchedItem);
        setItemVariantUnavailable(false);
      } else {
        setSelectedItem(null);
        setItemVariantUnavailable(true);
      }
    }
  }, [selectedOptions, itemData?.items]);

  useEffect(() => {
    if (selectedItem) {
      const basePrice = selectedItem.price;
      let modifiersTotal = 0;

      Object.values(itemData?.modifierGroups || {})
        .filter((group: any) =>
          selectedItem?.modifierGroupIds.includes(group.id)
        )
        .forEach((group: any) => {
          const selectedModifierIds = selectedModifiers[group.id];
          const selectedOptions = group.modifiers.filter((mod: any) =>
            Array.isArray(selectedModifierIds)
              ? selectedModifierIds.includes(mod.id)
              : mod.id === selectedModifierIds
          );
          selectedOptions.forEach((option: any) => {
            modifiersTotal += option.price;
          });
        });
      const updatedTotalPrice = (basePrice + modifiersTotal) * quantity;
      setTotalPrice(updatedTotalPrice);
    }
  }, [selectedModifiers, selectedItem, quantity, itemData?.modifierGroups]);

  useEffect(() => {
    if (selectedItem) {
      setIsLoading(false);
    }
  }, [selectedItem]);

  const handleOptionChange = (variantId: string, optionId: string) => {
    handleQuantityChange(1);
    setSelectedOptions({
      ...selectedOptions,
      [variantId]: optionId,
    });
  };

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    const group = itemData?.modifierGroups?.[groupId];

    if (!group) return;

    if (isSingleSelection) {
      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: modifierId,
      });
    } else {
      const currentSelections = selectedModifiers[groupId] || [];
      const isSelected = currentSelections.includes(modifierId);

      if (!isSelected && currentSelections.length >= group.maxAllowed) {
        return;
      }

      const newSelections = isSelected
        ? currentSelections.filter((id: string) => id !== modifierId)
        : [...currentSelections, modifierId];

      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: newSelections,
      });
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= Number.MAX_SAFE_INTEGER) {
      setQuantity(newQuantity);
    }
  };

  const validateMinimumSelections = () => {
    return Object.values(itemData?.modifierGroups || {})
      .filter((group: any) => selectedItem?.modifierGroupIds.includes(group.id))
      .every((group: any) => {
        const selectedModifierIds = selectedModifiers[group.id] || [];
        const isMinimumModifiersMet = Array.isArray(selectedModifierIds)
          ? selectedModifierIds.length >= group.minRequired
          : selectedModifierIds
          ? true
          : false;
        return isMinimumModifiersMet;
      });
  };

  const handleAddToCart = () => {
    let finalSelectedModifiers = [];
    if (
      itemData?.modifierGroups &&
      Object.keys(itemData.modifierGroups).length !== 0
    ) {
      finalSelectedModifiers =
        Object.values(itemData?.modifierGroups).map((group: any) => {
          const selectedModifierIds = selectedModifiers[group.id];
          const selectedOptions = group.modifiers.filter((mod: any) =>
            Array.isArray(selectedModifierIds)
              ? selectedModifierIds.includes(mod.id)
              : mod.id === selectedModifierIds
          );

          return selectedOptions.map((option: any) => ({
            name: option.name,
            id: option.id,
            price: option.price,
            groupId: group.id,
          }));
        }) || [];
    }

    const newItem = {
      id: selectedItem.id,
      name: selectedItem.name,
      price: selectedItem.price,
      quantity,
      modifiers: finalSelectedModifiers.flat(),
      specialInstructions,
      subtotal: totalPrice,
      total: totalPrice,
      cartId: uuidv4(),
      isAgeRestricted: selectedItem.isAgeRestricted,
    };

    addToCart(newItem);
    setQuantity(1);
    setSpecialInstructions("");
    setSelectedModifiers({});
    window.dispatchEvent(new StorageEvent("storage", { key: "cart" }));
  };

  const productImage =
    selectedItem?.imageFilename ||
    (selectedItem?.images && selectedItem.images.length > 0
      ? selectedItem.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, selectedItem.images[0])
      : null);

  let disableAddButton =
    !selectedItem ||
    selectedItem?.available === false ||
    !isOpen ||
    !validateMinimumSelections();

  const isAgeRestricted =
    selectedItem?.isAgeRestricted || itemData?.isAgeRestricted;

  return (
    <div className="flex flex-col items-center pt-6 w-full">
      <div className="flex flex-col sm:flex-row justify-center sm:gap-20 px-8 w-full">
        <div className="flex-shrink-0 w-full sm:w-[420px]">
          <div
            className="relative overflow-hidden rounded-md bg-gray-200 mb-8"
            style={{ paddingBottom: "133.33%" }}
          >
            <Image
              width="0"
              height="0"
              sizes="100vw"
              alt={selectedItem?.name || itemData.name}
              src={productImage ? productImage.source : DefaultImage}
              className="absolute inset-0 w-full h-full object-cover"
              priority
            />
          </div>
        </div>
        <div className="sm:w-[40%]">
          <h1 className="text-4xl font-bold mb-4 break-words">
            {selectedItem?.name || itemData?.name || "Unnamed Item"}
          </h1>
          <p className="text-[28px] font-semibold mb-4">
            {selectedItem ? (
              <>
                <span
                  className={`${!selectedItem.available ? "line-through" : ""}`}
                >
                  ${(selectedItem.price / 100).toFixed(2)}
                </span>
                {!selectedItem.available && (
                  <span className="ml-4">Unavailable</span>
                )}
              </>
            ) : (
              "Price not available"
            )}
          </p>
          {isAgeRestricted && (
            <AgeRestricted
              text="Requires valid government-issued ID upon delivery or pickup"
              textClassName="text-[16px]"
            />
          )}
          <p className="text-[#637381] font-normal text-base mb-4 break-words whitespace-pre-wrap">
            {selectedItem?.description ||
              itemData?.description ||
              "No description available."}
          </p>
          {itemData.variants?.map((variant: any) => (
            <div key={variant.id} className="mb-4">
              <label className="block font-medium text-base mb-2">
                {variant.name}
              </label>
              <Popover className="relative w-[242px]">
                <PopoverButton className="flex items-center gap-x-2 w-full leading-6 text-gray-900 hover:bg-gray-50">
                  <div
                    id="item_data_variants"
                    className="w-full h-[48px] flex flex-row justify-between items-center px-5 py-3 border border-[#1F2A37] rounded-lg"
                  >
                    <p>
                      {variant.options.find(
                        (option: any) =>
                          option.id === selectedOptions[variant.id]
                      )?.name || `Select ${variant.name}`}
                    </p>
                    <DropdownArrowIcon />
                  </div>
                </PopoverButton>
                <PopoverPanel
                  transition
                  className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[560px] overflow-y-auto"
                >
                  <div className="w-full">
                    {variant.options.map((option: any) => (
                      <CloseButton
                        key={option.id}
                        className="group w-full relative flex flex-row items-start gap-x-6 rounded-lg px-4 py-4 text-sm leading-6 hover:bg-gray-50 cursor-pointer"
                        onClick={() =>
                          handleOptionChange(variant.id, option.id)
                        }
                      >
                        <p className=""> {option.name}</p>
                      </CloseButton>
                    ))}
                  </div>
                </PopoverPanel>
              </Popover>
            </div>
          ))}
          <ModifierGroupDisplay
            {...{
              itemData,
              selectedItem,
              selectedModifiers,
              handleModifierChange,
              merchantColor,
            }}
          />
          <div className="mb-4 flex flex-col items-start">
            <label
              htmlFor="input-quantity"
              className="font-medium text-base pb-4"
            >
              Quantity
            </label>
            <div className="flex flex-row">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                className="px-3 h-9 py-1 border rounded-l-md text-[20px] flex items-center"
              >
                -
              </button>
              <input
                id="input-quantity"
                value={quantity}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value))}
                className="w-12 h-9 text-center border-t border-b border-gray-300 appearance-none"
                min={1}
                style={{
                  appearance: "textfield",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                }}
                inputMode="numeric"
              />
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                className="px-3 h-9 py-1 border rounded-r-md text-[20px] flex items-center"
              >
                +
              </button>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-lg font-semibold mb-4">
              Special Instructions
            </label>
            <textarea
              className="w-full p-2 border rounded-md max-h-[500px]"
              value={specialInstructions}
              maxLength={200}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Provide information here"
            />
          </div>
          {checkoutEnabled && !isPreview && (
            <PrimaryButton
              style={{
                backgroundColor: disableAddButton ? "#C7C7C7" : merchantColor,
              }}
              onClick={handleAddToCart}
              className="w-full text-white disabled:text-black py-2 rounded disabled:bg-[#E5E5E5]"
              disabled={disableAddButton}
            >
              {selectedItem?.available === false ||
              (!isOpen &&
                isShopPage &&
                merchant.isService &&
                merchant.isHybrid) ||
              itemVariantUnavailable
                ? "Sorry, this item is currently unavailable. "
                : "Add to Cart"}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="w-[96%] my-4 pb-4 border-b border-b-[#D1D5DB]/50"></div>
    </div>
  );
};

const ModifierGroupDisplay = ({
  itemData,
  selectedItem,
  selectedModifiers,
  handleModifierChange,
  merchantColor,
}: any) => {
  return (
    <>
      {Object.values(itemData?.modifierGroups || {})
        .filter((group: any) =>
          selectedItem?.modifierGroupIds.includes(group.id)
        )
        .map((group: any) => {
          const selectedModifierIds = selectedModifiers[group.id] || [];
          const isMaxReached = selectedModifierIds.length >= group.maxAllowed;

          return (
            <div key={group.id} className="mb-4">
              <label className="block font-medium text-base mb-2">
                {group.name}
              </label>
              <div>
                <ModifierSelection
                  {...{
                    group,
                    selectedModifiers,
                    selectedModifierIds,
                    isMaxReached,
                    handleModifierChange,
                    merchantColor,
                  }}
                />
              </div>
            </div>
          );
        })}
    </>
  );
};

const ModifierSelection = ({
  group,
  selectedModifiers,
  selectedModifierIds,
  isMaxReached,
  handleModifierChange,
  merchantColor,
}: any) => {
  return (
    <>
      {group.modifiers.map((modifier: any) => {
        const isSingleSelection =
          group.minRequired === 1 && group.maxAllowed === 1;
        const isSelected = isSingleSelection
          ? selectedModifiers[group.id] === modifier.id
          : selectedModifierIds.includes(modifier.id);

        const isDisabled = !isSingleSelection && isMaxReached && !isSelected;

        return (
          <div
            key={modifier.id}
            className={`mb-2 ${
              isDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <label className="flex items-center justify-between">
              <div className="flex items-center">
                {isSingleSelection ? (
                  <input
                    type="radio"
                    name={group.id}
                    value={modifier.id}
                    checked={isSelected}
                    onChange={() =>
                      handleModifierChange(
                        group.id,
                        modifier.id,
                        isSingleSelection
                      )
                    }
                    className="mr-2"
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      accentColor: merchantColor,
                    }}
                  />
                ) : (
                  <input
                    type="checkbox"
                    name={group.id}
                    value={modifier.id}
                    checked={isSelected}
                    onChange={() =>
                      handleModifierChange(
                        group.id,
                        modifier.id,
                        isSingleSelection
                      )
                    }
                    disabled={isDisabled}
                    className="mr-2"
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      accentColor: merchantColor,
                    }}
                  />
                )}
                <span>{modifier.name}</span>
              </div>
              <span className="text-right">
                ${(modifier.price / 100).toFixed(2)}
              </span>
            </label>
          </div>
        );
      })}
    </>
  );
};
